import React, { FC, useEffect, useRef, useState } from "react";
import useSizeLimitModal from "components/Pages/Home/Helpers/useSizeLimitModal.tsx";
import fields from "./reuploaderFormFields";
import Form from "../Form/Form.js";
import qq from "fine-uploader/lib/core/s3.js";
import stripHtmlTags from "utils/trimHtmlTagsFunc.ts";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useUser } from "contexts/userContext/userContext.js";
import { useUploader } from "contexts/uploaderContext/uploaderContext.js";
import {
  beforeUploadAction,
  checkUploadLimit,
} from "components/Pages/Home/Helpers/uploaderAPI.js";
import { useComments } from "contexts/commentsContext/commentsContext.js";
import ReuploadErrorFilesComponent, {
  IFile,
} from "./ReuploadErrorFilesComponent.tsx";
import SimpleContent from "components/Structure/ModalManager/SimpleContent.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import { useNavigate } from "react-router-dom";

interface IFileFromUploader {
  name: string;
  size: number;
  id: number;
}

const ReuploadSideBar: FC = () => {
  const { getTextByKey, getLanguageCulture } = useContent();

  const { getUserData } = useUser();

  const { addComment } = useComments();

  const {
    uploaderGetFiles,
    getUploading,
    getHideSubmit,
    getMailId,
    getSize,
    getUploadId,
    setUploading,
    setHideSubmit,
    getIsUppy,
    getSaved,
    uplaoderStartUpload,
    uploaderSetMeta,
    uploaderRemoveFile,
  } = useUploader();

  const { setModal } = useModal();

  const navigate = useNavigate();

  const getFormValuesRef = useRef(null);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const modalHandler = useSizeLimitModal({
    getFormValuesRef,
  });

  const isErrorPage = window.location.pathname.includes("uploading-error");

  useEffect(() => {
    if (!getUploading()) {
      if (uploaderGetFiles().length > 0) {
        setHideSubmit(false);
      } else {
        setHideSubmit(true);
      }
    }
  }, [uploaderGetFiles().length]);

  const handleAddComment = (content: string, recaptchaResponse: string) => {
    const trimmedContent = stripHtmlTags(content || "");
    if (trimmedContent.length === 0) {
      navigate(".", { replace: true });
      return;
    }
    addComment({
      relevantFile: null,
      parent: null,
      content: content,
      user: getUserData(),
      mailId: getMailId(),
      recaptchaResponse,
      reupload: true,
    });
    navigate("?commentAdded=true");
  };

  // handleUploadedFilesRemove - return false if we need to stop submit
  const handleUploadedFilesRemove = () => {
    if (uploadedFiles.length > 0) {
      const repeatableFiles = uploaderGetFiles().filter(
        (file: IFileFromUploader) =>
          uploadedFiles.some(
            (uploadefFile: IFile) =>
              uploadefFile.name === file.name && uploadefFile.size === file.size
          )
      );

      if (repeatableFiles.length > 0) {
        repeatableFiles.forEach((file: IFileFromUploader) => {
          uploaderRemoveFile(file.id);
        });
        setModal({
          modal: SimpleContent,
          props: {
            title: getTextByKey("reupload-files-exist-title"),
            message: getTextByKey("reupload-files-exist-message"),
          },
        });
      }
    }
    return true;
  };

  // submit form logic
  const handleSubmit = async (body: {
    userEmail: string;
    userMessage: string;
    recaptchaResponse: string;
  }) => {
    handleUploadedFilesRemove();

    if (uploaderGetFiles().length === 0) return;

    uploaderSetMeta({
      getUniqueId: qq.getUniqueId,
      addWaterMark: false,
      filesOwnerEmail: body.userEmail,
    });

    const res = await checkUploadLimit({
      totalSize: getSize(),
      uploadId: getUploadId(),
      userdId: getUserData().UserId,
      modalHandler,
      mailId: getMailId(),
    });
    // res will come only in case size limit is not exceed
    if (res?.status === "success") {
      setUploading(true);
      setHideSubmit(true);
      // doing request to server to set all settings relevant to this mail and get confirmation that uploading allowed
      // if yes it will start upload
      const totalSize = getIsUppy()
        ? getSize()
        : uploaderGetFiles().reduce(
            (acc: number, file: { size: number }) => acc + file.size,
            0
          );

      //@ts-ignore
      const submitRes = await beforeUploadAction({
        files: uploaderGetFiles(),
        uploadId: getUploadId(),
        lang: getLanguageCulture(),
        userMail: body.userEmail,
        totalSize: totalSize,
        uplaoderStartUpload: uplaoderStartUpload,
        code: null,
        isSaved: getSaved(),
        isReupload: true,
      });
      body.userMessage &&
        handleAddComment(body.userMessage, body.recaptchaResponse);
      return submitRes;
    } else {
      return res;
    }
  };

  return (
    <div
      className={`reupload-form-container${getUploading() ? " uploading" : ""}`}
    >
      {isErrorPage ? (
        <ReuploadErrorFilesComponent
          submitProps={{
            title: getTextByKey("send-files"),
            hidden: getHideSubmit(),
          }}
          handleSubmit={handleSubmit}
          setUploadedFiles={setUploadedFiles}
          uploadedFiles={uploadedFiles}
        />
      ) : (
        <Form
          fieldsProps={{ field: "none", label: "moveable " }}
          fields={isErrorPage ? {} : fields}
          onSubmit={handleSubmit}
          submitProps={{
            title: getTextByKey("send-files"),
            hidden: getHideSubmit(),
          }}
          defaultValuesObj={{ userEmail: getUserData().UserName }}
          captchaProvider="cloudflare"
          buttonProps={{ buttonsSize: "large" }}
          checkAndReturnValuesFunc={{
            ref: getFormValuesRef,
            noValidate: true,
          }}
          // captchaProvider="cloudflare"
          // cloudflareActionName="add_comment_reupload"
        />
      )}
    </div>
  );
};

export default ReuploadSideBar;
